import clsx from 'clsx'
import { type KeyboardEvent, type ReactNode } from 'react'
import { AccountRecord } from '../../api/client'
import { faIconName } from '../../util/classNames'
import { getAccountDisplayName, getAccountSecondaryLabel } from '../../util/account-utils'

type Props = {
    account: AccountRecord
    currentAccountId: number
    isSelected?: boolean
    isHighlighted?: boolean
    rightContent?: ReactNode
    onAccountClick: (account: AccountRecord, disabled: boolean) => void
    onAccountKeyDown: (
        event: KeyboardEvent<HTMLDivElement>,
        account: AccountRecord,
        disabled: boolean
    ) => void
}

/**
 * Displays a single account option within the Cross Profile Engage popover.
 * Callers inject any trailing controls via `rightContent`, keeping the row reusable across flows.
 */
export default function CrossProfileEngageAccountRow(props: Props) {
    const {
        account,
        currentAccountId,
        isSelected = false,
        isHighlighted = false,
        rightContent,
        onAccountClick,
        onAccountKeyDown
    } = props

    const isDisabled = !account.hasAuthorisation
    const highlightRow = isSelected || isHighlighted
    const isCurrent = account.id === currentAccountId

    const iconServiceName = account.serviceAccountUsername?.startsWith('u_')
        ? 'reddit'
        : account.socialMediaServiceShortName?.toLowerCase()
    const socialMediaIconName = iconServiceName ? faIconName(iconServiceName, false) : ''

    const rowClasses = clsx(
        'flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition',
        isDisabled
            ? 'cursor-not-allowed opacity-50'
            : 'cursor-pointer hover:border-gray-300 hover:bg-gray-50',
        highlightRow
            ? 'border-primary-200 bg-primary-50 text-primary-800 shadow-sm'
            : 'border-gray-200 text-gray-700'
    )

    return (
        <li key={account.id}>
            <div
                className={rowClasses}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                onClick={() => onAccountClick(account, isDisabled)}
                onKeyDown={(event) => onAccountKeyDown(event, account, isDisabled)}
                aria-pressed={isSelected}
                aria-disabled={isDisabled}
            >
                <div className="flex min-w-0 items-start gap-3">
                    {socialMediaIconName && (
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-base text-gray-600">
                            <i className={`fab ${socialMediaIconName}`} aria-hidden="true" />
                            <span className="sr-only">
                                {account.socialMediaServiceDisplayName || iconServiceName}
                            </span>
                        </span>
                    )}
                    <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-950">
                            {getAccountDisplayName(account)}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            {getAccountSecondaryLabel(account) && (
                                <span>{getAccountSecondaryLabel(account)}</span>
                            )}
                            {isCurrent && (
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600">
                                    Current profile
                                </span>
                            )}
                            {isDisabled && (
                                <span className="rounded-full bg-yellow-100 px-2 py-0.5 font-medium text-yellow-800">
                                    Needs authorisation
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {rightContent && (
                    <div className="ml-4 flex shrink-0 items-center gap-2">{rightContent}</div>
                )}
            </div>
        </li>
    )
}
